import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

const ALERT_COLORS = {
  high:   { bg: '#faeef4', color: '#9b2c5e', dot: '#9b2c5e' },
  medium: { bg: '#fef3e2', color: '#b45309', dot: '#b45309' },
  low:    { bg: '#eaf1fb', color: '#1a4e8c', dot: '#1a4e8c' },
};

const ALERT_ICONS = {
  large_amount:     '💰',
  frequency:        '📈',
  suspicious_pattern: '🔍',
  kyc_mismatch:     '🪪',
  blacklist:        '⛔',
  velocity:         '⚡',
};

export default function AdminCompliance() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState('');
  const [resolving, setResolving] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/compliance/alerts');
      setAlerts(Array.isArray(res.data) ? res.data : []);
      setLastRefresh(new Date());
    } catch { setAlerts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const handleResolve = async () => {
    if (!selected || !resolution.trim()) { alert('Escribe la resolución'); return; }
    setResolving(true);
    try {
      await api.post(`/api/v1/compliance/alerts/${selected.id}/resolve`, { resolution });
      setAlerts(al => al.map(a => a.id === selected.id ? { ...a, resolved: true, resolution } : a));
      setSelected(prev => ({ ...prev, resolved: true, resolution }));
      setResolution('');
    } catch { alert('Error al resolver la alerta'); }
    finally { setResolving(false); }
  };

  const filtered = alerts.filter(a =>
    filter === 'all' ? true :
    filter === 'pending' ? !a.resolved :
    a.resolved
  );

  const pending = alerts.filter(a => !a.resolved).length;

  return (
    <div style={{ display: selected ? 'grid' : 'block', gridTemplateColumns: '1fr 400px', gap: 18 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Compliance</h1>
            <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>
              {pending} alertas pendientes · Auto-refresh 60s · {lastRefresh.toLocaleTimeString('es-ES')}
            </p>
          </div>
          <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            ↻ Actualizar
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Pendientes', value: alerts.filter(a => !a.resolved).length, color: '#9b2c5e', bg: '#faeef4' },
            { label: 'Resueltas', value: alerts.filter(a => a.resolved).length, color: '#2d6a4f', bg: '#eaf4ef' },
            { label: 'Total', value: alerts.length, color: '#7a7060', bg: '#f0ebe2' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 28, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['pending', 'Pendientes'], ['resolved', 'Resueltas'], ['all', 'Todas']].map(([f, label]) => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: filter === f ? '#c94a1e' : '#f0ebe2', color: filter === f ? '#fff' : '#7a7060' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 18 }}>Sin alertas {filter === 'pending' ? 'pendientes' : ''}</div>
          </div>
        ) : filtered.map((alert, i) => {
          const severity = alert.severity || 'medium';
          const sc = ALERT_COLORS[severity] || ALERT_COLORS.medium;
          const isSelected = selected?.id === alert.id;
          return (
            <div key={i}
              onClick={() => setSelected(isSelected ? null : alert)}
              style={{ background: '#fff', border: `1px solid ${isSelected ? '#c94a1e' : '#e2dbd0'}`, borderRadius: 12, padding: '16px 18px', marginBottom: 8, cursor: 'pointer', opacity: alert.resolved ? 0.7 : 1 }}
              onMouseOver={e => { if (!isSelected) e.currentTarget.style.background = '#f9f6f1'; }}
              onMouseOut={e => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 22 }}>{ALERT_ICONS[alert.alert_type] || '⚠️'}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                      {alert.alert_type?.replace(/_/g, ' ') || 'Alerta'}
                    </div>
                    <div style={{ fontSize: 12, color: '#7a7060' }}>
                      TX: <span style={{ fontFamily: 'monospace', color: '#c94a1e' }}>{alert.transaction_ref || '—'}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flex: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.color }}>
                    {severity.toUpperCase()}
                  </span>
                  {alert.resolved ? (
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#eaf4ef', color: '#2d6a4f' }}>✓ Resuelta</span>
                  ) : (
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#faeef4', color: '#9b2c5e' }}>Pendiente</span>
                  )}
                </div>
              </div>
              {alert.description && (
                <div style={{ fontSize: 12, color: '#7a7060', marginTop: 8, paddingLeft: 32 }}>{alert.description}</div>
              )}
              <div style={{ fontSize: 11, color: '#b0a090', marginTop: 6, paddingLeft: 32 }}>
                {alert.created_at ? new Date(alert.created_at).toLocaleString('es-ES') : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22, height: 'fit-content', position: 'sticky', top: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 17 }}>Detalle alerta</div>
            <button onClick={() => { setSelected(null); setResolution(''); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#7a7060' }}>✕</button>
          </div>

          <div style={{ fontSize: 28, textAlign: 'center', marginBottom: 12 }}>
            {ALERT_ICONS[selected.alert_type] || '⚠️'}
          </div>

          {[
            ['Tipo', selected.alert_type?.replace(/_/g, ' ')],
            ['Severidad', selected.severity?.toUpperCase() || 'MEDIUM'],
            ['TX Ref', selected.transaction_ref || '—'],
            ['Usuario ID', selected.user_id || '—'],
            ['Monto', selected.amount_usd ? `$${selected.amount_usd}` : '—'],
            ['Creada', selected.created_at ? new Date(selected.created_at).toLocaleString('es-ES') : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0ebe2', fontSize: 13 }}>
              <span style={{ color: '#7a7060' }}>{k}</span>
              <span style={{ fontWeight: 600, fontFamily: k === 'TX Ref' || k === 'Usuario ID' ? 'monospace' : 'inherit', fontSize: k === 'TX Ref' || k === 'Usuario ID' ? 11 : 13 }}>{v}</span>
            </div>
          ))}

          {selected.description && (
            <div style={{ background: '#fef3e2', borderRadius: 8, padding: '10px 12px', margin: '14px 0', fontSize: 13, color: '#b45309' }}>
              {selected.description}
            </div>
          )}

          {selected.resolved ? (
            <div style={{ background: '#eaf4ef', borderRadius: 8, padding: '12px 14px', marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#2d6a4f', marginBottom: 4 }}>✓ RESUELTA</div>
              <div style={{ fontSize: 13, color: '#2d6a4f' }}>{selected.resolution}</div>
            </div>
          ) : (
            <div style={{ borderTop: '1px solid #e2dbd0', paddingTop: 16, marginTop: 14 }}>
              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 8 }}>
                Resolución
              </label>
              <textarea
                value={resolution}
                onChange={e => setResolution(e.target.value)}
                placeholder="Describe la acción tomada: verificado con cliente, falso positivo, bloqueado usuario..."
                style={{ width: '100%', padding: '10px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: 80, marginBottom: 10 }}
              />
              <button onClick={handleResolve} disabled={resolving || !resolution.trim()} style={{ width: '100%', padding: '11px', borderRadius: 8, border: 'none', background: resolving || !resolution.trim() ? '#e2dbd0' : '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: resolving || !resolution.trim() ? 'not-allowed' : 'pointer' }}>
                {resolving ? 'Resolviendo...' : '✓ Marcar como resuelta'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
